/**
 * External storage checklist for pages that create observations outside the observation forms
 * (the exoplanet transit pages), so their users choose destinations the same way (#1474):
 *
 * - the selected project's storage and the user's personal storage are checked and locked, since
 *   the server adds them to every new observation;
 * - organization storage an org admin marked "Selected by default" (#1454) starts checked and may
 *   be unchecked;
 * - other organization storage is opt-in.
 *
 * Callers send `selectedIds` as `external_storage_destinations`, always as an array, so the server
 * knows the user made a choice and does not add an unchecked org default back.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Chip, FormControlLabel, Typography } from '@mui/material';
import { apiService } from '../../services/apiService';
import {
  flattenAvailableStorage,
  requiredStorageIds,
  withDefaultSelectedStorage,
} from '../../utils/storageDefaults';

const PROVIDER_LABELS = {
  dropbox: 'Dropbox',
  gdrive: 'Google Drive',
  'aws-s3': 'AWS S3',
  sftp: 'SFTP/SCP',
  gcs: 'GCS',
};

/** Loads the user's available storage and tracks the selection for `projectId`. */
export const useStorageDestinations = (projectId) => {
  const [configs, setConfigs] = useState([]);
  const [chosenIds, setChosenIds] = useState([]);

  useEffect(() => {
    let cancelled = false;
    apiService.getAvailableStorageConfigs()
      .then(response => {
        if (cancelled || !response?.success) return;
        const all = flattenAvailableStorage(response.data);
        setConfigs(all);
        setChosenIds(prev => withDefaultSelectedStorage(prev, all));
      })
      .catch(() => { /* Non-critical: the user may have no storage configured */ });
    return () => { cancelled = true; };
  }, []);

  const visibleConfigs = useMemo(() => configs.filter(c =>
    c._scope !== 'project' || (projectId && String(c._scopeId) === String(projectId))
  ), [configs, projectId]);

  const requiredIds = useMemo(() => requiredStorageIds(configs, projectId), [configs, projectId]);

  // Only destinations that apply to this project, plus the required ones.
  const selectedIds = useMemo(() => {
    const visible = new Set(visibleConfigs.map(c => c._id));
    return Array.from(new Set([...chosenIds.filter(id => visible.has(id)), ...requiredIds]));
  }, [chosenIds, visibleConfigs, requiredIds]);

  const toggle = (id) => {
    if (requiredIds.includes(id)) return;
    setChosenIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  return { visibleConfigs, selectedIds, requiredIds, toggle };
};

/** The checklist for a `useStorageDestinations()` result. Renders nothing without storage. */
const StorageDestinationPicker = ({ storage, dense = false }) => {
  const { visibleConfigs, selectedIds, requiredIds, toggle } = storage;
  if (visibleConfigs.length === 0) return null;

  const fontSize = dense ? '0.75rem' : undefined;
  return (
    <Box>
      <Typography variant={dense ? 'caption' : 'subtitle2'} sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
        External storage
      </Typography>
      {visibleConfigs.map(config => {
        const required = requiredIds.includes(config._id);
        return (
          <FormControlLabel
            key={config._id}
            control={
              <Checkbox
                size={dense ? 'small' : 'medium'}
                checked={selectedIds.includes(config._id)}
                disabled={required}
                onChange={() => toggle(config._id)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontSize }}>
                  {config.display_name}
                  <Chip label={PROVIDER_LABELS[config.provider] || config.provider || 'Storage'}
                    size="small" variant="outlined" sx={{ ml: 1 }} />
                  <Chip label={config._scope === 'personal' ? 'Personal' : `${config._scope === 'organization' ? 'Org' : 'Project'}: ${config._scopeLabel}`}
                    size="small" color={config._scope === 'organization' ? 'info' : 'secondary'} sx={{ ml: 0.5 }} />
                  {required && <Chip label="Required" size="small" color="primary" sx={{ ml: 0.5 }} />}
                </Typography>
                {!dense && (config.destination_path || config.custom_path) && (
                  <Typography variant="caption" color="text.secondary">
                    {[config.destination_path, config.custom_path].filter(Boolean).join('/')}
                    {config.delete_from_minio && ' (will delete from server after transfer)'}
                  </Typography>
                )}
              </Box>
            }
            sx={{ display: 'flex', alignItems: 'flex-start', mb: dense ? 0 : 0.5 }}
          />
        );
      })}
    </Box>
  );
};

export default StorageDestinationPicker;
